import { AxiosInstancePlus, CancelApi, RequestConfig } from '@tencent/oneid-request';
import { TRequestConfig } from 'common/apiRequest/Request';
import { UPLOAD_IMG_KEY } from 'common/constants/Constants';
import { IResponseType } from 'common/constants/Types';

export default abstract class BaseApi {
  /** axios实例 */
  protected abstract axiosInstance: AxiosInstancePlus;

  /** 具体模块url前缀 */
  protected abstract prefixURL: string;

  /**
   * 查询所有数据
   */
  async getAll(): Promise<IResponseType> {
    return await this.axiosInstance.get(this.prefixURL);
  }

  /**
   * 根据id查询数据
   * @param id string
   */
  public getById(id: string): Promise<IResponseType> {
    return this.axiosInstance.get(`${this.prefixURL}/${id}`, { skipShowError: true });
  }

  /**
   * 根据查询数据
   * @param params object
   */
  public getByParams(params: TRequestConfig['params']): Promise<IResponseType> {
    return this.axiosInstance.get(this.prefixURL, { params });
  }

  /**
   * 根据id删除数据
   * @param id string
   */
  public deleteById(id: string): Promise<IResponseType> {
    return this.axiosInstance.delete(`${this.prefixURL}/${id}`);
  }

  /**
   * 根据id更新数据
   * @param id string
   * @param bodyData object
   * @param configs object
   */
  public patchById(
    id: string,
    bodyData: TRequestConfig['data'],
    configs?: Record<string, any>,
  ): Promise<IResponseType> {
    return this.axiosInstance.patch(`${this.prefixURL}/${id}`, bodyData, configs);
  }

  /**
   * 根据内容创建数据
   * @param bodyData object
   * @param configs object
   */
  public create(
    bodyData: TRequestConfig['data'],
    configs?: Record<string, any>,
  ): Promise<IResponseType> {
    return this.axiosInstance.post(this.prefixURL, bodyData, configs);
  }

  /**
   * 发送表单数据
   * @param config RequestConfig
   */
  async sendFormData(config: RequestConfig): Promise<IResponseType<Record<string, any>>> {
    const { method = 'post', headers, onUploadProgress, ...otherParams } = config;

    return await this.axiosInstance.request({
      method,
      headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
      ...otherParams,
    });
  }

  /**
   * 获取公有桶上传图片token
   */
  getUploadToken(params: {
    upload_resource: string;
    resource_type: string;
    business_info?: {
      business_id: string;
    };
  }): Promise<IResponseType<{ cos_url: string; put_auth: string; cos_id: string }>> {
    return this.axiosInstance.post(`${this.prefixURL}/image/upload_auth`, params, {
      autoAbort: false,
    });
  }

  /**
   * 上传图片到cos桶
   * @param
   * @returns
   */
  putImgToCos(params: { cos_url: string; cos_auth: string; img: File }): Promise<IResponseType> {
    return this.axiosInstance.request({
      baseURL: params.cos_url,
      method: 'put',
      headers: {
        'Content-Type': params.img.type,
        // 'Content-Length': params.img.size.toString(),
        Authorization: params.cos_auth,
      },
      data: params.img,
      autoAbort: false,
    });
  }

  /**
   * 上传图片到cos桶流程，返回未审核的cosUrl链接
   */
  async uploadImg({
    upload_resource,
    img,
    business_info,
  }: {
    upload_resource: string;
    img: File;
    business_info?: {
      business_id: string;
    };
  }): Promise<null | { cosUrl: string; cosId: string }> {
    if (!UPLOAD_IMG_KEY.has(upload_resource)) {
      return null;
    }
    const resourceType = `${img.name.match(/\.[a-zA-Z]+$/)?.[0]}`;
    const cosRes = await this.getUploadToken({
      upload_resource,
      resource_type: resourceType,
      business_info,
    });
    if (m.get(cosRes, 'errCode')) {
      return null;
    }
    const { cos_url: cosUrl, put_auth: putAuth, cos_id: cosId } = cosRes;
    const putCosRet = await this.putImgToCos({
      cos_url: cosUrl,
      cos_auth: putAuth,
      img,
    });
    if (m.get(putCosRet, 'errCode')) {
      return null;
    }
    return { cosUrl, cosId };
  }

  /**
   * 获取图片审核结果
   */
  getSafeCheckStatus(img_url: string, options?: RequestConfig): Promise<CancelApi> {
    return this.axiosInstance.get(`${this.prefixURL}/image/get_safe_check_status`, {
      params: {
        img_url,
      },
      ...options,
    });
  }
}
